/**
 * Trial box quantity restriction.
 *
 * Enforces a maximum quantity of 1 for any product tagged `trial-box`,
 * across the PDP, the cart page and the cart drawer. Two independent layers:
 *
 * 1. UI clamp (this file, `input`/`change` listener below) - catches a
 *    quantity typed directly into a restricted `.qty-input__input` (the
 *    native `max="1"` attribute set in snippets/quantity-input.liquid stops
 *    the +/- stepper already, since HTMLInputElement.stepUp()/stepDown()
 *    clamp to max/min on their own, but it does not stop someone typing "5").
 *
 * 2. Cart-level self-heal (the `<trial-box-cart-guard>` element below) -
 *    catches everything the UI clamp can't: a second "Add to cart" click
 *    merging into an existing trial-box line, or a quantity set via a
 *    direct call to the storefront Cart AJAX API. The corrections it acts
 *    on are computed server-side in snippets/cart-items.liquid, because
 *    that's the only place with access to `item.product.tags` - the raw
 *    /cart.js response doesn't include product tags, so this script can't
 *    (and doesn't try to) identify restricted lines by itself.
 *
 * Limitation worth knowing about: the cart-level self-heal runs when this
 * snippet next renders (page load, or the sections Shopify returns from
 * /cart/add.js and /cart/change.js). A customer who calls the Cart API
 * directly and goes straight to checkout without the theme rendering
 * anything in between won't be caught by theme code at all - that gap can
 * only be closed with a Shopify Function (cart/checkout validation),
 * which is a separate app extension outside the theme.
 */
(function () {
  if (window.__trialBoxLimitInit) return;
  window.__trialBoxLimitInit = true;

  /**
   * Finds the closest place to show a friendly inline error for a given
   * quantity-input: the product form's alert box on the PDP, or the
   * line-item's own error slot in the cart/cart drawer.
   */
  function getErrorElement(quantityInputEl) {
    var cartItemRow = quantityInputEl.closest('.cart-item');
    if (cartItemRow) {
      return cartItemRow.querySelector('.cart-item__error');
    }

    var form = quantityInputEl.closest('product-form');
    if (form) {
      return form.querySelector('.js-form-error');
    }

    return document.getElementById('cart-errors');
  }

  function showMessage(quantityInputEl, message) {
    var errorEl = getErrorElement(quantityInputEl);
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.hidden = false;
  }

  /**
   * Clamps a manually typed/pasted quantity back to 1 on any restricted
   * quantity input, before the value can reach the cart or the "Add to
   * cart" submit. Delegated on document so it also covers inputs added
   * later (cart drawer re-renders, quick add, etc).
   */
  function handleQuantityInput(event) {
    var input = event.target;
    if (!input.matches || !input.matches('.qty-input__input')) return;

    var wrapper = input.closest('quantity-input');
    if (!wrapper || wrapper.dataset.trialBox !== 'true') return;

    var value = parseInt(input.value, 10);
    if (!isNaN(value) && value > 1) {
      input.value = '1';
      showMessage(input, wrapper.dataset.limitMessage || (window.theme && window.theme.strings && window.theme.strings.trialBoxLimitError) || 'Only 1 Trial Box can be purchased per order.');
    }
  }

  document.addEventListener('input', handleQuantityInput);
  document.addEventListener('change', handleQuantityInput);

  /**
   * Applies the cart-wide corrections computed in Liquid (see
   * snippets/cart-items.liquid) by calling the same /cart/change.js
   * endpoint the theme's own cart-items.js uses, then refreshes the cart
   * UI and surfaces the limit message.
   */
  if (!customElements.get('trial-box-cart-guard')) {
    class TrialBoxCartGuard extends HTMLElement {
      connectedCallback() {
        var corrections = [];
        try {
          corrections = JSON.parse(this.dataset.corrections || '[]');
        } catch (e) {
          corrections = [];
        }

        if (!corrections.length) return;

        this.applyCorrections(corrections);
      }

      async applyCorrections(corrections) {
        var requestOpts = {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          }
        };

        try {
          // Each correction targets a line by its stable `id` (the cart's
          // line-item key), not its position - positions shift as soon as
          // one correction in the batch removes a line, so they're applied
          // sequentially and by id to stay correct through that.
          for (var i = 0; i < corrections.length; i++) {
            requestOpts.body = JSON.stringify(corrections[i]);
            var response = await fetch(window.theme.routes.cartChange, requestOpts);
            if (!response.ok) throw new Error(response.status);
            await response.json();
          }

          this.showCartError(this.dataset.message);

          // Let the existing cart UI (icon bubble, subtotal, drawer/page
          // markup) catch up with the corrected quantities.
          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
        } catch (error) {
          console.log(error);
          this.showCartError((window.theme && window.theme.strings && window.theme.strings.cartError) || 'There was an error updating your cart.');
        }
      }

      showCartError(message) {
        var errorEl = document.getElementById('cart-errors');
        if (!errorEl || !message) return;
        errorEl.textContent = message;
        errorEl.hidden = false;
      }
    }

    customElements.define('trial-box-cart-guard', TrialBoxCartGuard);
  }
})();
